import { InstanceBatch } from "../../core/batching/instance_batch.js";
import { ShaderBuilder } from "../../shader_builder/shader_builder.js";
import { AttributeSize, BlendMode } from "../../core/types/webgl_types.js";
import { Vector2 } from "../../../common/vector2.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { WebGLStaticAtlasBuilder } from "../../core/rendering/textures/webgl_static_atlas_bulder.js";
import { Vfx2DGlobalAtlas } from "../../core/rendering/textures/vfx_2d_global_atlas.js";

export const FireworksSpriteLayout = {
  a_bp0: AttributeSize.Vec2,
  a_bp1: AttributeSize.Vec2,
  a_bp2: AttributeSize.Vec2,
  a_bp3: AttributeSize.Vec2,
  a_offset: AttributeSize.Vec2,
  a_anim_ranges: AttributeSize.Vec4, // start_size, end_size, start_alpha, end_alpha
  a_time_params: AttributeSize.Vec4, // start_time, duration, curve_t, breakpoint
  a_uv_bounds: AttributeSize.Vec4,   // min_u, min_v, max_u, max_v
  a_color: AttributeSize.Vec3,
  a_additive: AttributeSize.Float,
} as const;

export class VfxFireworksBatch extends InstanceBatch<typeof FireworksSpriteLayout>
{
  constructor( config?: { max_instances?: number; blend_mode?: BlendMode } )
  {
    super( {
      max_instances: config?.max_instances ?? 50000,
      vertices_per_instance: 6,
      primitive_type: WebGL2RenderingContext.TRIANGLES,
      blend_mode: config?.blend_mode ?? BlendMode.Additive, // Default to additive for fireworks
      layout: FireworksSpriteLayout,
    } );

    this.buildShader(
      new ShaderBuilder()
        .includeChunk( 'remap' )
        .includeChunk( 'cubicBezier' )
        .withUniform( 'sampler2D', 'u_texture' )
        .withVarying( 'vec2', 'v_uv' )
        .withVarying( 'vec3', 'v_color' )
        .withVarying( 'float', 'v_alpha' )
        .withVarying( 'float', 'v_additive' )
        .withVertexLogic( `
          // a_time_params: x = start_time, y = duration, z = curve_t, w = breakpoint
          // a_anim_ranges: x = start_size, y = end_size, z = start_alpha, w = end_alpha
          // a_uv_bounds: x = min_u, y = min_v, z = max_u, w = max_v
          
          float elapsed = u_time - a_time_params.x;
          float t = clamp(elapsed / a_time_params.y, 0.0, 1.0);
          
          float current_size = 0.0;
          float current_alpha = 0.0;
          float bp = a_time_params.w;
          
          if (bp <= 0.0) {
              current_size = remap(t, 0.0, 1.0, a_anim_ranges.x, a_anim_ranges.y, true);
              current_alpha = remap(t, 0.0, 1.0, a_anim_ranges.z, a_anim_ranges.w, true);
          } else {
              if (t <= bp) {
                  current_size = a_anim_ranges.x;
                  current_alpha = a_anim_ranges.z;
              } else {
                  current_size = remap(t, bp, 1.0, a_anim_ranges.x, a_anim_ranges.y, true);
                  current_alpha = remap(t, bp, 1.0, a_anim_ranges.z, a_anim_ranges.w, true);
              }
          }
          
          size = vec2(current_size, current_size);
          
          position = cubicBezier(a_bp0, a_bp1, a_bp2, a_bp3, a_time_params.z) + a_offset;

          v_uv = mix(a_uv_bounds.xy, a_uv_bounds.zw, uv);
          v_color = a_color;
          v_alpha = current_alpha;
          v_additive = a_additive;
          
          // Auto-kill when duration is exceeded (shrink to 0)
          if (elapsed < 0.0 || t >= 1.0) {
              size = vec2(0.0);
          }
        ` )
        .withFragmentLogic( `
          vec4 tex = texture(u_texture, v_uv);
          float alpha = tex.a * v_alpha;
          float out_alpha = alpha * (1.0 - v_additive);
          fragColor = vec4(tex.rgb * v_color * alpha, out_alpha);
        ` )
    );
  }

  protected override onInit( gl: WebGL2RenderingContext ): void
  {
    const atlas_data = Vfx2DGlobalAtlas.getAtlasData();
    const atlas = WebGLStaticAtlasBuilder.createAtlasFromData( gl, atlas_data );
    this.textures = [ { location: 'u_texture', texture: atlas.texture, unit: 0 } ];
  }

  protected override fillWarmupInstance( gpu_data: Float32Array, offset: number ): void
  {
    if ( !this.attr_offset ) return;

    // Fill valid dummy values for 1x1 scissor GPU warmup draw call
    gpu_data[offset + this.attr_offset.a_bp0] = 10;
    gpu_data[offset + this.attr_offset.a_bp0 + 1] = 10;
    gpu_data[offset + this.attr_offset.a_bp1] = 10;
    gpu_data[offset + this.attr_offset.a_bp1 + 1] = 10;
    gpu_data[offset + this.attr_offset.a_bp2] = 10;
    gpu_data[offset + this.attr_offset.a_bp2 + 1] = 10;
    gpu_data[offset + this.attr_offset.a_bp3] = 10;
    gpu_data[offset + this.attr_offset.a_bp3 + 1] = 10;

    gpu_data[offset + this.attr_offset.a_offset] = 0;
    gpu_data[offset + this.attr_offset.a_offset + 1] = 0;

    gpu_data[offset + this.attr_offset.a_anim_ranges] = 1;
    gpu_data[offset + this.attr_offset.a_anim_ranges + 1] = 1;
    gpu_data[offset + this.attr_offset.a_anim_ranges + 2] = 0;
    gpu_data[offset + this.attr_offset.a_anim_ranges + 3] = 0;

    gpu_data[offset + this.attr_offset.a_time_params] = performance.now();
    gpu_data[offset + this.attr_offset.a_time_params + 1] = 1000;
    gpu_data[offset + this.attr_offset.a_time_params + 2] = 0;
    gpu_data[offset + this.attr_offset.a_time_params + 3] = 0;

    gpu_data[offset + this.attr_offset.a_uv_bounds] = 0;
    gpu_data[offset + this.attr_offset.a_uv_bounds + 1] = 0;
    gpu_data[offset + this.attr_offset.a_uv_bounds + 2] = 1;
    gpu_data[offset + this.attr_offset.a_uv_bounds + 3] = 1;

    gpu_data[offset + this.attr_offset.a_color] = 1;
    gpu_data[offset + this.attr_offset.a_color + 1] = 1;
    gpu_data[offset + this.attr_offset.a_color + 2] = 1;

    gpu_data[offset + this.attr_offset.a_additive] = 0;
  }

  protected override updateInstance( gpu_data: Float32Array, offset: number, progress: number, now: number, idx: number ): boolean
  {
    // Return false to remove if the particle has expired
    const start_time = gpu_data[offset + this.attr_offset.a_time_params];
    const duration = gpu_data[offset + this.attr_offset.a_time_params + 1];
    return (now - start_time) < duration;
  }

  /**
   * Створює нову частинку фейєрверку.
   */
  public spawnParticle(
    img_id: ImgID,
    bp0: Vector2,
    bp1: Vector2,
    bp2: Vector2,
    bp3: Vector2,
    offset_vec: Vector2,
    curve_t: number,
    color: { x: number; y: number; z: number },
    start_size: number,
    end_size: number,
    start_alpha: number,
    end_alpha: number,
    breakpoint: number,
    additive: number,
    start_time: number,
    duration: number
  ): number
  {
    const idx = this.allocateInstanceIndex( start_time, duration );
    if ( idx === -1 ) return -1;

    const atlas_data = Vfx2DGlobalAtlas.getAtlasData();
    const bounds = atlas_data.getUVRect( img_id );

    const data = this.buffer.getDataArray();
    const offset = idx * this.buffer.floats_per_instance;

    data[offset + this.attr_offset.a_bp0] = bp0.x;
    data[offset + this.attr_offset.a_bp0 + 1] = bp0.y;
    data[offset + this.attr_offset.a_bp1] = bp1.x;
    data[offset + this.attr_offset.a_bp1 + 1] = bp1.y;
    data[offset + this.attr_offset.a_bp2] = bp2.x;
    data[offset + this.attr_offset.a_bp2 + 1] = bp2.y;
    data[offset + this.attr_offset.a_bp3] = bp3.x;
    data[offset + this.attr_offset.a_bp3 + 1] = bp3.y;
    data[offset + this.attr_offset.a_offset] = offset_vec.x;
    data[offset + this.attr_offset.a_offset + 1] = offset_vec.y;
    
    data[offset + this.attr_offset.a_anim_ranges] = start_size;
    data[offset + this.attr_offset.a_anim_ranges + 1] = end_size;
    data[offset + this.attr_offset.a_anim_ranges + 2] = start_alpha;
    data[offset + this.attr_offset.a_anim_ranges + 3] = end_alpha;

    data[offset + this.attr_offset.a_time_params] = start_time;
    data[offset + this.attr_offset.a_time_params + 1] = duration;
    data[offset + this.attr_offset.a_time_params + 2] = curve_t;
    data[offset + this.attr_offset.a_time_params + 3] = breakpoint;

    data[offset + this.attr_offset.a_uv_bounds] = bounds.min_x;
    data[offset + this.attr_offset.a_uv_bounds + 1] = bounds.min_y;
    data[offset + this.attr_offset.a_uv_bounds + 2] = bounds.max_x;
    data[offset + this.attr_offset.a_uv_bounds + 3] = bounds.max_y;
    
    data[offset + this.attr_offset.a_color] = color.x;
    data[offset + this.attr_offset.a_color + 1] = color.y;
    data[offset + this.attr_offset.a_color + 2] = color.z;
    
    data[offset + this.attr_offset.a_additive] = additive;

    return this.getStableId( idx );
  }
}
